import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, ErrorNote, Field, Input, MessageBox, Panel, Select, StatusBadge } from "@/components/kmt/ui";
import { errMsg, useLoad, useOwnerApi } from "@/lib/api";
import { ct, prettyPhone } from "@/lib/format";
import { PageTitle } from "./DashLayout";

export default function Customers() {
  const api = useOwnerApi();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const list = useLoad(() => api.get("/customers", { params: { q: q || undefined } }).then((r) => r.data), [api, q]);
  return (
    <div>
      <PageTitle title="Customers" sub="Phone numbers are stored as +1XXXXXXXXXX." />
      <div className="grid items-start gap-6 xl:grid-cols-[1fr_1.3fr]">
        <Panel>
          <Input aria-label="Search customers" placeholder="Search name or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search" />
          <ul className="mt-4 divide-y divide-gold/15" data-testid="customers-list">
            {(list.data || []).map((c: any) => (
              <li key={c.id}>
                <button onClick={() => setSel(c.id)} data-testid={`customer-row-${c.phone}`}
                  className={`flex min-h-[56px] w-full items-center justify-between gap-3 px-2 py-3 text-left transition-colors duration-200 hover:text-gold ${sel === c.id ? "text-gold" : ""}`}>
                  <span>{c.name}<br /><span className="text-[14px] text-mist/65">{prettyPhone(c.phone)}{c.alt_phones.length > 0 && ` +${c.alt_phones.length}`}</span></span>
                  <span className="text-[14px] text-mist/70">{c.booking_count} rides · {c.agreement_on_file ? "Signed" : "Not signed"}</span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>
        {sel && <CustomerDetail id={sel} all={list.data || []} onMerged={(id) => { setSel(id); list.reload(); }} />}
      </div>
    </div>
  );
}

function CustomerDetail({ id, all, onMerged }: { id: string; all: any[]; onMerged: (id: string) => void }) {
  const api = useOwnerApi();
  const { data: c, setData, error } = useLoad(() => api.get(`/customers/${id}`).then((r) => r.data), [api, id]);
  const [phone, setPhone] = useState("");
  const [src, setSrc] = useState("");
  const [link, setLink] = useState<any>(null);
  const [err, setErr] = useState("");
  if (error) return <p className="text-red-200">{error}</p>;
  if (!c) return null;
  const act = async (fn: () => Promise<any>, ok: string) => {
    setErr("");
    try { await fn(); toast.success(ok); } catch (e) { setErr(errMsg(e)); }
  };
  const others = all.filter((x) => x.id !== id).map((x) => ({ value: x.id, label: `${x.name} · ${prettyPhone(x.phone)}` }));
  return (
    <Panel title={c.name} data-testid="customer-detail">
      <p className="text-[15px]">Primary: {prettyPhone(c.phone)}</p>
      {c.alt_phones.length > 0 && <p className="text-[15px] text-mist/75" data-testid="customer-alt-phones">Linked: {c.alt_phones.map(prettyPhone).join(", ")}</p>}
      <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto]">
        <Field label="Link another phone number"><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="customer-link-phone-input" /></Field>
        <Btn variant="ghost" className="self-end" onClick={() => act(async () => { setData((await api.post(`/customers/${id}/phones`, { phone })).data); setPhone(""); }, "Phone linked")} data-testid="customer-link-phone-btn">Link</Btn>
      </div>
      {others.length > 0 && (
        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto]">
          <Field label="Merge another customer into this one"><Select options={[{ value: "", label: "Choose customer" }, ...others]} value={src} onChange={(e) => setSrc(e.target.value)} data-testid="customer-merge-select" /></Field>
          <Btn variant="ghost" className="self-end" disabled={!src} onClick={() => act(async () => { setData((await api.post("/customers/merge", { source_id: src, target_id: id })).data); setSrc(""); onMerged(id); }, "Customers merged")} data-testid="customer-merge-btn">Merge</Btn>
        </div>
      )}
      <ErrorNote msg={err} />
      <div className="gold-rule my-6" />
      <Btn onClick={() => act(async () => setLink((await api.post("/agreements/links", { customer_id: id, origin: window.location.origin })).data), "Signing link created")} data-testid="customer-create-link-btn">Create signing link</Btn>
      {link && <div className="mt-4"><MessageBox text={link.sms_text} phone={link.phone} testid="customer-agreement-message" /></div>}
      <h4 className="mt-8 font-display text-gold">Agreements</h4>
      <ul className="mt-2 text-[15px]">
        {c.agreements.map((a: any) => <li key={a.id} className="py-1">{a.version} · {a.status}{a.signed && ` · ${ct(a.signed.signed_at)}`}</li>)}
        {!c.agreements.length && <li className="text-mist/60">None yet</li>}
      </ul>
      <h4 className="mt-6 font-display text-gold">Rides</h4>
      <ul className="mt-2 divide-y divide-gold/15">
        {c.bookings.map((b: any) => (
          <li key={b.id}><Link to={`/dashboard/bookings/${b.id}`} className="flex min-h-[48px] items-center justify-between gap-3 hover:text-gold">{b.ref} · {ct(b.pickup_at)} <StatusBadge status={b.status} label={b.status_label} /></Link></li>
        ))}
      </ul>
    </Panel>
  );
}
